import { useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import Overlay from './Overlay'
import MessageBox from './MessageBox'
import ReceiptAttentionModal from './AttentionModal'
import ReceiptInfoModal from './ReceiptInfoModal'

import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const QUICKBOX_MESSAGES_REFRESH_EVENT = 'quickbox-messages-refresh'

const ReceiptUploadModal = ({
  open,
  onClose,
  onSaved,
  userId,
  initialTripId = '',
  initialVehicleId = '',
  lockTripSelection = false
}) => {
  const { t, i18n } = useTranslation()

  const [uploading, setUploading] = useState(false)
  const [message, setMessage] = useState(null)
  const [saved, setSaved] = useState(false)

  const [vehicleId, setVehicleId] = useState('')
  const [vehicleError, setVehicleError] = useState(false)
  const [vehiclesById, setVehiclesById] = useState({})
  const [tripId, setTripId] = useState(initialTripId ? String(initialTripId) : '')
  const [trips, setTrips] = useState([])

  const isVehicleLocked = Boolean(initialVehicleId)
  const isTripLocked = Boolean(initialTripId)

  const [file, setFile] = useState(null)
  const [fileError, setFileError] = useState(false)

  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)

  const [supportedBrands, setSupportedBrands] = useState([])
  const [infoOpen, setInfoOpen] = useState(false)

const [tooltipTimer, setTooltipTimer] = useState(null)
const [showFullscreenHint, setShowFullscreenHint] = useState(false)
const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 })

  const loadVehicles = useCallback(async () => {
    if (!userId) return

    try {
      const res = await fetchWithAuth(`/users/${userId}/vehicles`)
      if (!res.ok) return

      const data = await res.json()
      if (!Array.isArray(data)) return

      const index = data.reduce((acc, vehicle) => {
        if (vehicle?.id == null) return acc

        acc[String(vehicle.id)] = vehicle
        return acc
      }, {})

      setVehiclesById(index)
    } catch {}
  }, [userId])

  const loadTrips = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/triplog/trips')
      if (!res.ok) return

      const data = await res.json()
      if (!Array.isArray(data)) return

      setTrips(data)
    } catch {}
  }, [])

  const loadSupportedBrands = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/receipt/supported-brands')

      if (!res.ok) return

      const data = await res.json()

      if (!Array.isArray(data)) return

      setSupportedBrands(data)
    } catch {}
  }, [])

  const filteredTrips = useMemo(() => {
    if (!vehicleId) return []

    return trips.filter((trip) => {
      if (String(trip?.vehicleId) !== String(vehicleId)) return false

      const assignedReceiptId = trip?.receiptId ?? trip?.receipt_id ?? null

      return !assignedReceiptId
    })
  }, [vehicleId, trips])

  useEffect(() => {
    if (!open) return

    setMessage(null)
    setFile(null)
    setFileError(false)
    setVehicleId(initialVehicleId ? String(initialVehicleId) : '')
    setVehicleError(false)
    setHoverBtn(null)
    setPressedBtn(null)
    setSaved(false)
    setTripId(initialTripId ? String(initialTripId) : '')

    loadVehicles()
    loadTrips()
    loadSupportedBrands()
  }, [
    open,
    loadVehicles,
    loadTrips,
    loadSupportedBrands,
    initialTripId,
    initialVehicleId
  ])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape' || !open || infoOpen) return

      e.preventDefault()
      e.stopPropagation()
      onClose()
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, onClose, infoOpen])

  useEffect(() => {
    if (isTripLocked) return
    if (!tripId) return

    const tripStillAvailable = filteredTrips.some(
      (trip) => String(trip.id) === String(tripId)
    )

    if (!tripStillAvailable) {
      setTripId('')
    }
  }, [tripId, filteredTrips, isTripLocked])

  const handleFileSelected = (e) => {
    const f = e.target.files?.[0]

    if (!f) return

    setFileError(false)
    setMessage(null)
    setFile(f)
  }

  const saveUpload = async () => {
    let hasError = false

    if (!file) {
      setFileError(true)
      hasError = true
    }

    if (!vehicleId) {
      setVehicleError(true)
      hasError = true
    }

    if (hasError) return

    try {
      setUploading(true)
      setMessage(null)

      const fd = new FormData()

      fd.append('file', file)
      fd.append('vehicleId', vehicleId)

      if (tripId) {
        fd.append('tripId', tripId)
      }

const res = await fetchWithAuth(
  '/receipt/upload',
  {
    method: 'POST',
    body: fd
  },
  i18n.language
)

if (!res.ok) {
  const err = await res.json().catch(() => null)

  setMessage({
    type: 'error',
    message:
      err?.code === 'UPLOAD_COUNTRY_LIMIT'
        ? t('TRIPLOG.RECEIPTS.UPLOAD_COUNTRY_LIMIT_MSG')
        : err?.message || err?.error || t('vehicle_save_failed')
  })

  return
}

      setMessage({
        type: 'success',
        message: (
          <>
            <div>{t('TRIPLOG.RECEIPTS.UPLOAD_SUCCESS_TITLE')}</div>
            <div>{t('TRIPLOG.RECEIPTS.UPLOAD_SUCCESS_MSG')}</div>
          </>
        )
      })

setSaved(true)

let refreshCount = 0

const interval = setInterval(() => {
  window.dispatchEvent(
    new Event(QUICKBOX_MESSAGES_REFRESH_EVENT)
  )

  refreshCount++

  if (refreshCount >= 10) {
    clearInterval(interval)
  }
}, 2000)
    } catch {
      setMessage({
        type: 'error',
        message: t('CONNECTION_ERROR')
      })
    } finally {
      setUploading(false)
    }
  }

  if (!open) return null

  const baseButtonColor = fileError ? '#f6caca' : '#f5f5f5'

  return (
    <>
      <Overlay open={uploading} zIndex={4000} backdropOpacity={0.75} />

      <ReceiptInfoModal
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        supportedBrands={supportedBrands}
      />

      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 4000
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#ededed',
            padding: 20,
            border: '2px solid #107c6b',
            borderRadius: 12,
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            width: 'clamp(320px, 30vw, 420px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <h3
            style={{
              fontSize: 20,
              marginBottom: 16,
              textAlign: 'center'
            }}
          >
            {t('TRIPLOG.VEHICLES.SELECT')}
          </h3>

          <select
            disabled={saved || isVehicleLocked}
            value={vehicleId ?? ''}
            onChange={(e) => {
              setVehicleId(e.target.value || '')
              setVehicleError(false)
            }}
            style={{
              width: 240,
              height: 34,
              borderRadius: 6,
              border: vehicleError ? '1px solid #c33' : '1px solid #999',
              backgroundColor:
                saved || isVehicleLocked
                  ? '#ddd'
                  : vehicleError
                    ? '#f6caca'
                    : '#ffffff',
              padding: '0 8px',
              marginBottom: 18
            }}
          >
            <option value="">--</option>

            {Object.values(vehiclesById).map((v) => (
              <option key={v.id} value={v.id}>
                {`${v.name || v.model || '-'} (${v.licensePlate || v.spz || '-'})`}
              </option>
            ))}
          </select>

          {!lockTripSelection && (
            <select
              disabled={saved || isTripLocked || lockTripSelection}
              value={tripId}
              onChange={(e) => setTripId(e.target.value || '')}
              style={{
                width: 240,
                height: 34,
                borderRadius: 6,
                border: '1px solid #999',
                backgroundColor:
                  saved || isTripLocked || lockTripSelection
                    ? '#ddd'
                    : '#ffffff',
                padding: '0 8px',
                marginBottom: 18
              }}
            >
		<option value="">
  		{t('TRIPLOG.TRIPS.TABLE.tripId', {
 		   defaultValue: t('TRIPLOG.TAB.NO_TRIP')
 		 })}
		</option>

              {filteredTrips.map((trip) => {
                const date = trip.date
                  ? new Date(trip.date).toLocaleDateString('sk-SK')
                  : '-'

                const vehicle = vehiclesById[String(trip.vehicleId)]

                const vehicleLabel =
                  vehicle?.name ||
                  vehicle?.model ||
                  vehicle?.licensePlate ||
                  vehicle?.spz ||
                  '-'

                return (
                  <option key={trip.id} value={trip.id}>
                    #{trip.tripNumber} · {date} · {vehicleLabel} ·{' '}
                    {trip.startPlace || '-'} → {trip.endPlace || '-'}
                  </option>
                )
              })}
            </select>
          )}

          <input
            id="vehicle-upload-input"
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={handleFileSelected}
            style={{ display: 'none' }}
          />

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
              marginBottom: 16
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10
              }}
            >
<ReceiptAttentionModal
  onClick={() => {
    if (tooltipTimer) clearTimeout(tooltipTimer)
    setShowFullscreenHint(false)
    setInfoOpen(true)
  }}
  onMouseEnter={() => {
    const timer = setTimeout(() => {
      setShowFullscreenHint(
        t('SUPPORTED_RECEIPTS_LIST')
      )
    }, 600)

    setTooltipTimer(timer)
  }}
  onMouseLeave={() => {
    if (tooltipTimer) clearTimeout(tooltipTimer)
    setShowFullscreenHint(false)
    setTooltipTimer(null)
  }}
  onMouseMove={(e) =>
    setCursorPos({
      x: e.clientX,
      y: e.clientY
    })
  }
/>

              <button
                disabled={saved}
                onClick={() => {
                  if (saved) return

                  document
                    .getElementById('vehicle-upload-input')
                    .click()
                }}
                onMouseEnter={(e) => {
                  if (saved) return

                  e.currentTarget.style.background = '#9ad1bb'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = baseButtonColor
                  e.currentTarget.style.transform = 'scale(1)'
                  e.currentTarget.style.boxShadow =
                    '0 1px 8px rgba(0,0,0,0.30)'
                }}
                onMouseDown={(e) => {
                  if (saved) return

                  e.currentTarget.style.transform = 'scale(0.96)'
                  e.currentTarget.style.boxShadow =
                    '0 0px 4px rgba(0,0,0,0.25)'
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'scale(1)'
                  e.currentTarget.style.boxShadow =
                    '0 1px 8px rgba(0,0,0,0.30)'

                  e.currentTarget.blur()
                }}
                style={{
                  border: '1.5px solid #107c6b',
                  background: baseButtonColor,
                  borderRadius: 6,
                  height: 28,
                  padding: '0 16px',
                  cursor: saved ? 'default' : 'pointer',
                  fontWeight: 700,
                  boxShadow: '0 1px 8px rgba(0,0,0,0.30)',
                  transition:
                    'transform 0.05s ease, box-shadow 0.1s ease',
                  outline: 'none',
                  opacity: saved ? 0.6 : 1
                }}
              >
                {t('vehicle_select_file')}
              </button>
            </div>

            <span
              style={{
                fontSize: 14,
                color: file ? '#107c6b' : '#888'
              }}
            >
              {file ? file.name : t('vehicle_no_file')}
            </span>
          </div>

          {message && (
            <div
              style={{
                width: '100%',
                marginBottom: 14
              }}
            >
              <MessageBox
                message={message.message}
                type={message.type}
              />
            </div>
          )}

          <div
            style={{
              display: 'flex',
              gap: 12
            }}
          >
            {!saved && (
              <>
                <button
                  style={modalPrimaryButtonStyle(
                    pressedBtn,
                    'saveUpload',
                    hoverBtn === 'saveUpload'
                  )}
                  onClick={saveUpload}
                >
                  {t('SAVE')}
                </button>

                <button
                  style={modalSecondaryButtonStyle(
                    pressedBtn,
                    'cancelUpload',
                    hoverBtn === 'cancelUpload'
                  )}
                  onClick={onClose}
                >
                  {t('CANCEL')}
                </button>
              </>
            )}

            {saved && (
              <button
                style={modalPrimaryButtonStyle(
                  pressedBtn,
                  'okUpload',
                  hoverBtn === 'okUpload'
                )}
                onClick={() => {
                  onClose()
                  onSaved?.()
                }}
              >
                OK
              </button>
            )}
          </div>
        </div>
      </div>
{showFullscreenHint && (
  <div
    style={{
      position: 'fixed',
      top: cursorPos.y - 50,
      left: cursorPos.x,
      transform: 'translateX(-50%)',
      background: 'rgba(0,0,0,0.85)',
      color: '#d9ffe4',
      padding: '6px 14px 8px 14px',
      borderRadius: 12,
      fontSize: 16,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      zIndex: 5000,
      border: '1.5px solid #107c6b',
      boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
      display: 'flex',
      alignItems: 'center',
      lineHeight: 1
    }}
  >
    {showFullscreenHint}
  </div>
)}
    </>
  )
}

export default ReceiptUploadModal